// Keeps open tabs on the current build. A deploy replaces dist/ with new hashed file names, so a tab
// opened before it still asks for the old chunks: they 404 ("Failed to fetch dynamically imported
// module", "Unable to preload CSS"). index.html is served no-store, so a full page load gets the new build.
// - a chunk that fails to load reloads the page at once (at the route being opened, if any)
// - dist/version.json is checked every few minutes and when the tab comes back; a newer build makes the
//   next navigation a full page load, so nothing the customer is typing gets lost

const CHECK_INTERVAL = 5 * 60 * 1000
// A chunk missing from the new build too would reload forever: one reload per this many ms
const RELOAD_GUARD_MS = 30 * 1000
const RELOAD_KEY = 'update_reload_at'

let reloading = false
let updateAvailable = false
let pendingPath = null

// Sentry drops the errors of the old build while the page is being replaced
export const isReloadingForUpdate = () => reloading

const reloadTo = (path) => {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY))
    if (last && Date.now() - last < RELOAD_GUARD_MS) return false
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()))
  } catch {
    // No sessionStorage (private mode): reload without the guard
  }
  reloading = true
  window.location.assign(path)
  return true
}

const checkVersion = async () => {
  if (updateAvailable) return
  try {
    const response = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' })
    const { version } = await response.json()
    if (version && version !== __BUILD_ID__) updateAvailable = true
  } catch {
    // Offline, or no version.json yet (the SPA fallback answers with index.html)
  }
}

export default {
  install(app, { router }) {
    window.addEventListener('vite:preloadError', () => {
      reloadTo(pendingPath ?? window.location.href)
    })

    router.beforeEach((to, from) => {
      // A full page load at the new build instead of an in-app navigation (not on the first one)
      if (updateAvailable && from.matched.length && reloadTo(to.fullPath)) return false
      pendingPath = to.fullPath
    })
    router.afterEach(() => {
      pendingPath = null
    })

    if (!import.meta.env.PROD) return
    setInterval(() => {
      if (document.visibilityState === 'visible') checkVersion()
    }, CHECK_INTERVAL)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') checkVersion()
    })
  },
}
